import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Check } from 'lucide-react';
import HelpButton from '../common/HelpButton';
import { useToast, ToastContainer } from '../common/Toast';
import CAM360Badge from '../common/CAM360Badge';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const { error: showError, toasts, removeToast } = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cam-auth-page">
      <div className="cam-auth-container">
        <div className="cam-auth-card">
          {/* Left Section - Branding and Features */}
          <div className="cam-auth-left">
            <div className="cam-brand-content">
              <div className="cam-brand-logo">
                <div className="cam-logo-icon" style={{ backgroundColor: 'transparent', boxShadow: 'none', padding: '8px' }}>
                  <CAM360Badge size={100} />
                </div>
                <h1 className="cam-brand-title">CLM</h1>
                <p className="cam-brand-subtitle">Comprehensive Asset & Contract Lifecycle</p>
              </div>
              
              <ul className="cam-brand-features">
                {[
                  'AI-Powered Contract Intelligence',
                  'Contract Lifecycle Management',
                  'Real-time Analytics & Reporting',
                  'Compliance & Risk Management'
                ].map((feature, index) => (
                  <li key={index} className="cam-feature-item">
                    <div className="cam-feature-icon">
                      <Check size={12} color="white" />
                    </div>
                    <span className="cam-feature-text">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Help Center Button - Bottom Center */}
            {/* Opens CAM360HelpCenter in a new window */}
            <div className="cam-help-center-wrapper">
              <HelpButton 
                type="global"
                title="Help Center"
                variant="text"
                size={16}
                className="cam-help-center-btn"
                openInNewWindow={true}
              />
            </div>
          </div>

          {/* Right Section - Login Form */}
          <div className="cam-auth-right">
            <div className="cam-form-container">
              <div className="cam-form-header">
                <h2 className="cam-form-title">Welcome Back</h2>
                <p className="cam-form-subtitle">Sign in to your CLM workspace</p>
              </div>

              {error && (
                <div className="cam-error-alert">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="cam-form-group">
                  <label className="cam-form-label">Email Address</label>
                  <div className="cam-input-wrapper">
                    <Mail size={18} className="cam-input-icon" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      className="input-cam cam-input-with-icon"
                      required
                    />
                  </div>
                </div>

                <div className="cam-form-group">
                  <label className="cam-form-label">Password</label>
                  <div className="cam-input-wrapper">
                    <Lock size={18} className="cam-input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className="input-cam cam-input-with-icon cam-input-with-icon-right"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="cam-input-icon-right"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="cam-form-options">
                  <div className="cam-form-check">
                    <input
                      type="checkbox"
                      id="remember-me"
                      className="cam-form-check-input"
                    />
                    <label className="cam-form-check-label" htmlFor="remember-me">
                      Remember me
                    </label>
                  </div>
                  <Link to="/forgot-password" className="cam-forgot-link">
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  className="btn-cam w-100 mt-4"
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};


export default Login;
